import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { LCLShipmentModel } from '@data-layer/models/lcl';
import { enumLclTrackingStates } from '@enum/enumLclTrackingStates';
import { TranslateService } from '@ngx-translate/core';
import { CompanyModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';
import { LCLBillsRepository } from '@data-layer/repositories';
import { BillItemPendingModel, BillModel, PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { enumPaymentState } from '@data-layer/enums/payments';
import { LclShipmentService } from '@app/modules/lcl-shipping/services/lcl-shipment.service';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { CardApplyCouponComponent } from '@shared/cards/components/card-apply-coupon/card-apply-coupon.component';

@Component({
  selector: 'lcl-shipment-payment-card',
  templateUrl: './lcl-shipment-payment-card.component.html',
  styleUrls: ['./lcl-shipment-payment-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclShipmentPaymentCardComponent implements OnInit, OnChanges {
  @Input() public shipment: LCLShipmentModel;
  @Input() public pendingBills: PaymentBillItemPendingModel;
  @Input() public isCouponAvailable: boolean;

  @Output() public cancelPaymentSource: EventEmitter<any> = new EventEmitter<any>();
  @Output() public successPaymentSource: EventEmitter<{
    state: enumPaymentState;
    bill: BillModel;
  }> = new EventEmitter<{
    state: enumPaymentState;
    bill: BillModel;
  }>();
  @Output() public errorPaymentSource: EventEmitter<any> = new EventEmitter<any>();
  @Output() public errorCustomPaymentSource: EventEmitter<any> = new EventEmitter<any>();
  @Output() public applyCoupon: EventEmitter<string> = new EventEmitter<string>();

  public totalPrice: number;
  public enumPaymentState: typeof enumPaymentState = enumPaymentState;

  @ViewChild(CardApplyCouponComponent) private cardApplyCouponComponentRef: CardApplyCouponComponent;
  private company: CompanyModel;

  constructor(
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private billsRepository: LCLBillsRepository,
    private companiesService: CompaniesService,
    private lclShipmentService: LclShipmentService,
    private ga: GoogleAnalyticsService,
  ) {}

  public ngOnInit(): void {
    this.company = this.companiesService.getCompany();
    this.calculatePaymentPrice();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!!changes?.pendingBills) {
      this.calculatePaymentPrice();
    }
  }

  public onSuccessPayment(): void {
    this.lclShipmentService.updateLclShipment$.next();
  }

  public onErrorCustomPayment(): void {
    this.lclShipmentService.updateLclShipment$.next();
  }

  public onErrorCallback(): void {
    this.lclShipmentService.updateLclShipment$.next();
  }

  public onCancelCallback(): void {
    this.lclShipmentService.updateLclShipment$.next();
  }

  public setCouponError(errorMessage: string): void {
    this.cardApplyCouponComponentRef.setCouponError(errorMessage);
    this.cdr.markForCheck();
  }

  public get paymentHeaderDescription(): string {
    let text: string;

    if (this.shipment.paymentState === enumPaymentState.SUCCESS) {
      text = this.translateService.instant('LCL_SHIPMENT.PAYMENT_LABEL.ADDITIONAL_PAYMENTS');
    } else {
      if (this.isPaymentPending) {
        text = 'YOUR_FILING_IS_ON_HOLD';
      } else {
        text =
          this.shipment.trackingState > enumLclTrackingStates.AT_ORIGIN_WAREHOUSE &&
          this.shipment.trackingState < enumLclTrackingStates.AT_DESTINATION_WAREHOUSE
            ? 'LCL_SHIPMENT.PAYMENT_LABEL.FIRST_STATE_HEADER'
            : 'LCL_SHIPMENT.PAYMENT_LABEL.SECOND_STATE_HEADER';
      }
    }
    return this.translateService.instant(text);
  }

  public get paymentSubHeaderDescription(): string {
    if (this.isPaymentPending) {
      return;
    }

    let text: string = '';
    if (this.shipment.paymentState === enumPaymentState.SUCCESS) {
    } else {
      text =
        this.shipment.trackingState > enumLclTrackingStates.AT_ORIGIN_WAREHOUSE &&
        this.shipment.trackingState < enumLclTrackingStates.AT_DESTINATION_WAREHOUSE
          ? 'LCL_SHIPMENT.PAYMENT_LABEL.FIRST_STATE_SUBHEADER'
          : 'LCL_SHIPMENT.PAYMENT_LABEL.SECOND_STATE_SUBHEADER';
    }
    return text ? this.translateService.instant(text) : null;
  }

  public get isPaymentPending(): boolean {
    return this.shipment.paymentState === enumPaymentState.PENDING;
  }

  private calculatePaymentPrice(): void {
    this.totalPrice = this.pendingBills?.items?.reduce((accTotal: number, item: BillItemPendingModel) => {
      return accTotal + item.price * item.quantity;
    }, 0);
  }
}
